import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import { GraphQL } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'
import debounce from 'lodash.debounce'
import { BedDoubleIcon } from '@hugeicons/core-solid-rounded'

import * as Table from '@/components/ui/Table'
import Loading from '@/components/ui/Loading'
import usePaginatedSpaces, { QueryVariables } from '../hooks/usePaginatedSpaces'

interface Props {
  search: string
}

function LoadingRow() {
  return (
    <Table.Row className="pointer-events-none">
      <Table.Cell colSpan={7}>
        <div className="flex flex-row justify-center p-4">
          <Loading />
        </div>
      </Table.Cell>
    </Table.Row>
  )
}

export function SpaceTable(props: Props) {
  const { t } = useLingui()
  const { search } = props
  const [, navigate] = useLocation()

  const [variables, setVariables] = useState<QueryVariables>({
    filter: {
      search
    },
    sort: {
      direction: GraphQL.SortDirection.Asc,
      field: GraphQL.SpaceSortField.Identifier
    }
  })

  const debounceSearch = useMemo(
    () =>
      debounce(
        (search: string) =>
          setVariables(vars => ({
            ...vars,
            filter: { ...vars.filter, search }
          })),
        1000
      ),
    [setVariables]
  )

  useEffect(() => {
    if (variables.filter.search === search) return

    debounceSearch(search)
  }, [debounceSearch, search, variables])

  const {
    items: spaces,
    page,
    metadata,
    loading,
    loadPage
  } = usePaginatedSpaces(variables)

  const onEdit = useCallback(
    (event: MouseEvent, identifier: string) => {
      event.stopPropagation()

      navigate(`/space/${identifier}`)
    },
    [navigate]
  )

  const { totalPages, totalEntries } = useMemo(() => {
    return {
      totalPages: metadata?.totalPages,
      totalEntries: metadata?.totalEntries
    }
  }, [metadata])

  const hasResults = useMemo(
    () => !!totalEntries && totalEntries > 0,
    [totalEntries]
  )

  return (
    <Table.Root>
      <Table.Head>
        <Table.Row>
          <Table.Heading>
            <Trans>Room Number</Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>Name</Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>Description</Trans>
          </Table.Heading>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {spaces &&
          spaces.map(function (space) {
            return (
              <Table.Row
                key={space.identifier}
                className="cursor-pointer"
                onClick={event => onEdit(event, space.identifier)}
              >
                <Table.Cell>{space.identifier}</Table.Cell>
                <Table.Cell>{space.name}</Table.Cell>
                <Table.Cell>{space.description}</Table.Cell>
              </Table.Row>
            )
          })}
        {!hasResults && !loading && (
          <Table.Empty
            Icon={BedDoubleIcon}
            text={t`No Rooms found`}
            toText={t`Create Rooms`}
            to="/create-space"
          />
        )}
        {loading && <LoadingRow />}
      </Table.Body>
      {hasResults && page && (
        <Table.PaginationCaption
          totalEntries={totalEntries!}
          currentPage={page}
          totalPages={totalPages!}
          onChangePage={loadPage}
        />
      )}
    </Table.Root>
  )
}
